import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Eye,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  GraduationCap,
  School,
  MessageSquare,
} from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  studentAdmissionInquiryAPI,
  type StudentAdmissionInquiry,
} from "../../services/api";
import { formatDate } from "../../lib/utils";
import { CommonPagination } from "../../components/CommonPagination";

export default function StudentInquiries() {
  const [inquiries, setInquiries] = useState<StudentAdmissionInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedInquiry, setSelectedInquiry] =
    useState<StudentAdmissionInquiry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchInquiries = useCallback(
    async (page = currentPage, size = pageSize) => {
      setLoading(true);
      try {
        const res = await studentAdmissionInquiryAPI.list({
          page,
          page_size: size,
        });
        // Based on user response format: { data: { inquiries: [], count: 0 } }
        const inquiriesList = res.data?.inquiries ?? [];
        const count = res.data?.count ?? inquiriesList.length ?? 0;

        setInquiries(inquiriesList);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / size));
      } catch {
        toast.error("Failed to load student inquiries");
        setInquiries([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );

  useEffect(() => {
    fetchInquiries(currentPage, pageSize);
  }, [currentPage, pageSize, fetchInquiries]);

  const handleViewInquiry = (inquiry: StudentAdmissionInquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInquiry(null);
  };

  const tableColumns = useMemo<TableColumnDef<StudentAdmissionInquiry>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 160,
        renderCell: inquiry => (
          <span className="font-medium">{inquiry.full_name}</span>
        ),
      },
      {
        id: "contact",
        label: "Contact",
        width: 180,
        renderCell: inquiry => (
          <div className="flex flex-col text-sm">
            <span>{inquiry.mobile}</span>
            <span className="text-muted-foreground">{inquiry.email}</span>
          </div>
        ),
      },
      {
        id: "course",
        label: "Course",
        width: 160,
        renderCell: inquiry => (
          <div className="flex flex-col text-sm">
            <span>{inquiry.course_std_name ?? "—"}</span>
            <span className="text-muted-foreground">
              Score: {inquiry.last_result_percentage}%
            </span>
          </div>
        ),
      },
      {
        id: "location",
        label: "Location",
        width: 160,
        renderCell: inquiry =>
          [inquiry.preferred_state_name, inquiry.pincode]
            .filter(Boolean)
            .join(", ") ||
          inquiry.address ||
          "—",
      },
      {
        id: "submitted",
        label: "Submitted",
        width: 120,
        renderCell: inquiry => formatDate(inquiry.created_at),
      },
      {
        id: "actions",
        label: "Actions",
        width: 100,
        alwaysVisible: true,
        renderCell: inquiry => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleViewInquiry(inquiry)}
            title="View Details"
          >
            <Eye className="mr-1 h-4 w-4" />
            View
          </Button>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  // const getAuthStatusBadge = (verified: boolean) => {
  //   if (verified) {
  //     return (
  //       <Badge className="bg-green-600 hover:bg-green-700">
  //         <CheckCircle className="mr-1 h-3 w-3" /> Verified
  //       </Badge>
  //     );
  //   }
  //   return (
  //     <Badge variant="secondary">
  //       <Clock className="mr-1 h-3 w-3" /> Pending
  //     </Badge>
  //   );
  // };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Student Admission Inquiries
          </h1>
          <p className="text-muted-foreground">
            Manage and view all student interest submissions for admission
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-3 pb-4">
          <DataTable
            title={`All Inquiries (${totalCount})`}
            tableId="super-admin-student-inquiries"
            columns={tableColumns}
            data={inquiries}
            getRowKey={inquiry => inquiry.id}
            loading={loading}
            loadingMessage="Loading inquiries..."
            emptyMessage="No admission inquiries found"
            rowClassName={() => "hover:bg-gray-100"}
          />

          {totalCount > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inquiry Detail Dialog — aligned with Funding donation details modal */}
      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          setDialogOpen(open);
          if (!open) {
            setSelectedInquiry(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
          <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                  aria-hidden
                >
                  <GraduationCap className="h-5 w-5 text-[#8B3232]" />
                </span>
                Inquiry details
              </DialogTitle>
              {selectedInquiry && (
                <span
                  className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-sm font-semibold capitalize ${
                    selectedInquiry.auth_status
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-[#f0dede] bg-[#FDF7F7] text-[#8B3232]"
                  }`}
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      selectedInquiry.auth_status
                        ? "bg-emerald-500"
                        : "bg-[#c53030]"
                    }`}
                    aria-hidden
                  />
                  {selectedInquiry.auth_status ? "Verified" : "Pending"}
                </span>
              )}
            </div>
          </DialogHeader>

          {selectedInquiry && (
            <>
              <div className="max-h-[min(80vh,680px)] overflow-y-auto bg-white">
                <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5">
                  <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                    <div className="min-w-0 space-y-1 sm:flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                        Student name
                      </p>
                      <p className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
                        {selectedInquiry.full_name}
                      </p>
                      <p className="text-xs font-medium text-zinc-500">
                        Inquiry ID: {selectedInquiry.id}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12 md:gap-x-16">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Submitted
                        </p>
                        <p className="text-sm font-semibold text-zinc-900">
                          {formatDate(selectedInquiry.created_at)}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Course / standard
                        </p>
                        <p className="text-sm font-semibold text-zinc-900">
                          {selectedInquiry.course_std_name ?? "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <User className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Contact information
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <InquiryDetailItem
                        icon={<Phone className="h-3.5 w-3.5" />}
                        label="Phone number"
                        value={selectedInquiry.mobile}
                      />
                      <InquiryDetailItem
                        icon={<Mail className="h-3.5 w-3.5" />}
                        label="Email address"
                        value={selectedInquiry.email}
                      />
                      <InquiryDetailItem
                        icon={<Mail className="h-3.5 w-3.5" />}
                        label="Submitted by"
                        value={
                          selectedInquiry.submitted_by_email ??
                          "Direct public submission"
                        }
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <MapPin className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Address
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5">
                      <InquiryDetailItem
                        icon={<MapPin className="h-3.5 w-3.5" />}
                        label="Full address"
                        value={selectedInquiry.address}
                      />
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <InquiryDetailItem
                          icon={<MapPin className="h-3.5 w-3.5" />}
                          label="Pincode"
                          value={selectedInquiry.pincode}
                        />
                        <InquiryDetailItem
                          icon={<MapPin className="h-3.5 w-3.5" />}
                          label="Preferred state"
                          value={selectedInquiry.preferred_state_name ?? "—"}
                        />
                      </div>
                      <InquiryDetailItem
                        icon={<MapPin className="h-3.5 w-3.5" />}
                        label="Preferred zone"
                        value={selectedInquiry.preferred_zone_name ?? "—"}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <School className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      School & academic
                    </h3>
                    <hr className="mb-4 border-zinc-200" />
                    <div className="grid gap-5 md:grid-cols-3">
                      <InquiryDetailItem
                        icon={<School className="h-3.5 w-3.5" />}
                        label="College / school"
                        value={
                          selectedInquiry.college_school ??
                          "Private application"
                        }
                      />
                      <InquiryDetailItem
                        icon={<GraduationCap className="h-3.5 w-3.5" />}
                        label="Last result"
                        value={`${selectedInquiry.last_result_percentage}%`}
                      />
                      <InquiryDetailItem
                        icon={<Calendar className="h-3.5 w-3.5" />}
                        label="Last updated"
                        value={formatDate(selectedInquiry.updated_at)}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <MapPin className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Preferred cities & areas
                    </h3>
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50/40 p-4">
                      <div className="mb-4">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Cities
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedInquiry.preferred_cities_names?.length ? (
                            selectedInquiry.preferred_cities_names.map(c => (
                              <Badge
                                key={c}
                                variant="secondary"
                                className="border border-zinc-200 bg-white font-semibold text-zinc-800"
                              >
                                {c}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-zinc-500">
                              No city preference
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Areas
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedInquiry.preferred_areas_names?.length ? (
                            selectedInquiry.preferred_areas_names.map(a => (
                              <Badge
                                key={a}
                                variant="outline"
                                className="border-zinc-200 bg-white text-xs font-medium text-zinc-700"
                              >
                                {a}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-sm text-zinc-500">
                              No specific areas
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                        <MessageSquare className="h-4 w-4 text-[#8B3232]" />
                      </span>
                      Remarks
                    </h3>
                    <div className="space-y-4 rounded-lg border border-zinc-200 bg-[#fafafa] p-4">
                      <div>
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Last year note
                        </p>
                        <p className="border-l-[3px] border-[#8B3232] pl-4 text-sm font-medium leading-relaxed text-zinc-500">
                          &quot;
                          {selectedInquiry.last_year_remark ??
                            "No additional notes provided."}
                          &quot;
                        </p>
                      </div>
                      <div>
                        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Hostel remark
                        </p>
                        <p className="border-l-[3px] border-[#8B3232] pl-4 text-sm font-medium leading-relaxed text-zinc-500">
                          {selectedInquiry.hostel_remark ??
                            "No specific requests for hostel allocation."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                  onClick={handleCloseDialog}
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InquiryDetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400 [&_svg]:text-zinc-400">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="text-sm font-semibold text-zinc-900">{value}</p>
      </div>
    </div>
  );
}
